import { z } from 'zod';
import { ContractorSubtypeSchema } from '../lib/enums';
import { PermissionSchema } from '../lib/permissions';
import { zEmail, zIsoDateTime, zJurisdiction, zUuid } from './common';
import { CompanySchema, CompanyUserSchema } from './company';
/**
 * Auth flow (opaque bearer tokens; TOTP mandatory for owner/admin):
 *
 *   signup → company + owner created → login continuation says
 *   `totp_setup_required` (owner must set up TOTP before a full session).
 *   login → password ok → `totp_required` (or `totp_setup_required` for
 *   owner/admin without TOTP yet) → login/totp with the challenge token →
 *   full session tokens. Roles without mandatory TOTP get `ok` directly.
 */
export const PasswordSchema = z.string().min(10).max(200);
export const SessionTokensSchema = z.object({
    tokenType: z.literal('Bearer'),
    accessToken: z.string().min(20),
    accessExpiresAt: zIsoDateTime,
    refreshToken: z.string().min(20),
    refreshExpiresAt: zIsoDateTime,
});
// --------------------------------------------------------------------- signup
/**
 * The company half of a sign-up. Shared by the email and the Google sign-up so
 * both create exactly the same company from exactly the same rules.
 */
export const SignupCompanySchema = z
    .object({
    name: z.string().min(1).max(200),
    isContractor: z.boolean(),
    isSubcontractor: z.boolean(),
    contractorSubtype: ContractorSubtypeSchema.nullable().optional(),
    defaultJurisdiction: zJurisdiction.optional(),
    tradeProfileKeys: z.array(z.string().min(1).max(64)).max(20).optional(),
})
    .strict()
    .superRefine((v, ctx) => {
    if (!v.isContractor && !v.isSubcontractor) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'company must be a contractor, a subcontractor, or both',
        });
    }
    if (!v.isContractor && v.contractorSubtype != null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'contractorSubtype requires isContractor',
            path: ['contractorSubtype'],
        });
    }
});
export const SignupRequestSchema = z
    .object({
    company: SignupCompanySchema,
    user: z
        .object({
        name: z.string().max(200).optional(),
        email: zEmail,
        password: PasswordSchema,
    })
        .strict(),
})
    .strict();
// ---------------------------------------------------------------------- login
/**
 * Which surface is signing in (RolesUsersPlan §4.2/§4.7). Stored on the session;
 * a role not allowed on that surface is refused with `SURFACE_NOT_ALLOWED`.
 * Omitted → 'panel' (older clients keep working).
 */
export const LoginClientSchema = z.enum(['panel', 'mobile']);
/** Error code for a login refused because the role may not use that surface. */
export const SURFACE_NOT_ALLOWED = 'SURFACE_NOT_ALLOWED';
export const LoginRequestSchema = z
    .object({
    email: zEmail,
    password: z.string().min(1).max(200),
    client: LoginClientSchema.default('panel'),
})
    .strict();
export const LoginOkSchema = z.object({
    status: z.literal('ok'),
    tokens: SessionTokensSchema,
    user: CompanyUserSchema,
});
/** Password accepted; a TOTP code is required to finish login. */
export const LoginTotpRequiredSchema = z.object({
    status: z.literal('totp_required'),
    /** Short-lived opaque challenge token for POST /auth/login/totp. */
    totpToken: z.string().min(20),
});
/** Password accepted; role mandates TOTP but it is not set up yet. */
export const LoginTotpSetupRequiredSchema = z.object({
    status: z.literal('totp_setup_required'),
    totpToken: z.string().min(20),
});
export const LoginResponseSchema = z.discriminatedUnion('status', [
    LoginOkSchema,
    LoginTotpRequiredSchema,
    LoginTotpSetupRequiredSchema,
]);
export const SignupResponseSchema = z.object({
    company: CompanySchema,
    user: CompanyUserSchema,
    /** Continuation of the flow — same shape as a login response. */
    login: LoginResponseSchema,
});
// --------------------------------------------------------------------- google
/**
 * "Continue with Google". The client obtains a Google ID token (web button or
 * the native iOS sheet) and posts it here; the API verifies it and continues
 * exactly like a password login, except that a Google sign-in skips our own
 * two-factor for every role but platform Admin (Jonah, 2026-09-23).
 */
export const GoogleIdTokenSchema = z.string().min(20).max(4096);
export const GoogleLoginRequestSchema = z
    .object({
    idToken: GoogleIdTokenSchema,
    client: LoginClientSchema.default('panel'),
})
    .strict();
/**
 * Google verified the person but no account exists for them. `signupToken` is a
 * short-lived server-signed token carrying the verified identity; the sign-up
 * wizard collects the company details and posts it to /auth/google/signup. The
 * email and name here are for display — the server reads them from the token.
 */
export const LoginSignupRequiredSchema = z.object({
    status: z.literal('signup_required'),
    signupToken: z.string().min(20),
    email: zEmail,
    name: z.string().nullable(),
});
export const GoogleLoginResponseSchema = z.discriminatedUnion('status', [
    LoginOkSchema,
    LoginTotpRequiredSchema,
    LoginTotpSetupRequiredSchema,
    LoginSignupRequiredSchema,
]);
/**
 * POST /auth/google/check — verify a Google ID token WITHOUT signing anyone in.
 * Used by the join pages, which need a sign-up token for a new person and must
 * not mint a session they would only throw away.
 */
export const GoogleCheckRequestSchema = z.object({ idToken: GoogleIdTokenSchema }).strict();
export const GoogleCheckResponseSchema = z.discriminatedUnion('status', [
    LoginSignupRequiredSchema,
    z.object({ status: z.literal('account_exists'), email: zEmail }),
]);
/** POST /auth/google/signup — the same company as an email sign-up, no password. */
export const GoogleSignupRequestSchema = z
    .object({
    signupToken: z.string().min(20).max(4096),
    company: SignupCompanySchema,
    /** Optional override of the name Google supplied. The email is never taken from here. */
    user: z.object({ name: z.string().max(200).optional() }).strict().optional(),
    client: LoginClientSchema.default('panel'),
})
    .strict();
/** GET /auth/config — what the sign-in screens may offer. Client ids are public. */
export const AuthConfigResponseSchema = z.object({
    google: z.object({
        webClientId: z.string().nullable(),
        iosClientId: z.string().nullable(),
    }),
    /**
     * Whether a NEW company may be created (SIGNUP_MODE on the server). `open`
     * false = sign-ups closed: clients show "Sign-ups aren't open yet" instead of
     * the form. `paid` = open, but the company must subscribe before using the app.
     * Joining an existing company with a join code is unaffected either way.
     */
    signups: z.object({
        open: z.boolean(),
        paid: z.boolean(),
    }),
    /** Stripe subscriptions switched on (docs/BILLING.md). Off: no Billing page anywhere. */
    billing: z.object({ enabled: z.boolean() }).default({ enabled: false }),
});
/** Error code for any attempt to create a company while sign-ups are closed. */
export const SIGNUPS_CLOSED = 'SIGNUPS_CLOSED';
// ----------------------------------------------------------------------- totp
export const TotpCodeSchema = z.string().regex(/^\d{6}$/, 'expected 6-digit code');
export const TotpLoginRequestSchema = z
    .object({
    totpToken: z.string().min(20),
    code: TotpCodeSchema,
})
    .strict();
export const TotpLoginResponseSchema = LoginOkSchema;
/** Setup is reachable with a bearer session OR a totp_setup_required challenge token. */
export const TotpSetupRequestSchema = z
    .object({
    totpToken: z.string().min(20).optional(),
})
    .strict();
export const TotpSetupResponseSchema = z.object({
    secret: z.string().min(16),
    otpauthUrl: z.string().url(),
});
export const TotpActivateRequestSchema = z
    .object({
    totpToken: z.string().min(20).optional(),
    code: TotpCodeSchema,
})
    .strict();
export const TotpActivateResponseSchema = LoginOkSchema;
// ------------------------------------------------------------ session refresh
export const RefreshRequestSchema = z
    .object({
    refreshToken: z.string().min(20),
})
    .strict();
export const RefreshResponseSchema = z.object({
    tokens: SessionTokensSchema,
});
// ------------------------------------------------------------------- me + sessions
export const MeResponseSchema = z.object({
    user: CompanyUserSchema,
    company: CompanySchema,
    permissions: z.array(PermissionSchema),
    /**
     * Platform Admin act-as target (RolesUsersPlan §4.2); `company`/`permissions`
     * then describe the acting company. Absent/null when not acting.
     */
    actingCompany: z
        .object({
        id: zUuid,
        name: z.string(),
    })
        .nullable()
        .optional(),
});
export const SessionInfoSchema = z.object({
    id: zUuid,
    createdAt: zIsoDateTime,
    accessExpiresAt: zIsoDateTime,
    refreshExpiresAt: zIsoDateTime,
    revokedAt: zIsoDateTime.nullable(),
    ip: z.string().max(64).nullable(),
    userAgent: z.string().max(500).nullable(),
    /** True for the session making the request. */
    current: z.boolean(),
});
export const SessionsListResponseSchema = z.array(SessionInfoSchema);
//# sourceMappingURL=auth.js.map